import { useAutoneTranslation } from '@autone/translations';
import { getLocale } from '@autone/utils';
import { formatDistanceToNowStrict } from 'date-fns';
import { type FC, type PropsWithChildren } from 'react';

import { PopoverContent, Separator } from '../../../../../atoms';
import { type AutoneIconComponent } from '../../../../../icons/factory/create-icon-component';
import {
  type DatasetDataFreshnessStatus,
  type GeneratedDataFreshnessDataset,
  type UploadedDataFreshnessDataset,
} from '../../DataFreshnessWidget.types';
import {
  datasetIconMap,
  formatDatasetAge,
  getFreshnessStatusTextColor,
} from '../../utils';

import { datasetTranslationKeyMap } from './utils/data-source-translation-key-map';

interface DataFreshnessPopoverContentProps extends PropsWithChildren {
  uploadedDatasets: UploadedDataFreshnessDataset[];
  generatedDatasets: GeneratedDataFreshnessDataset[];
}

export const DataFreshnessPopoverContent: FC<
  DataFreshnessPopoverContentProps
> = ({ uploadedDatasets, generatedDatasets, children }) => {
  const { common, lang } = useAutoneTranslation();

  return (
    <PopoverContent
      className="dark w-72 flex flex-col gap-1"
      side="right"
      align="start"
    >
      <SectionHeader>
        {common('data-age.global-widget.uploaded-data')}
      </SectionHeader>
      <SectionBody>
        {uploadedDatasets.map((dataset) => (
          <Row
            key={dataset.name}
            Icon={datasetIconMap[dataset.name]}
            label={common(datasetTranslationKeyMap[dataset.name])}
            value={formatDatasetAge(dataset.ageDays, lang, common)}
            status={dataset.status}
          />
        ))}
      </SectionBody>
      {generatedDatasets.length > 0 && (
        <>
          <Separator className="my-1" />
          <SectionHeader>
            {common('data-age.global-widget.autone-generated-data')}
          </SectionHeader>
          <SectionBody>
            {generatedDatasets.map((dataset) => (
              <Row
                key={dataset.name}
                Icon={datasetIconMap[dataset.name]}
                label={common(datasetTranslationKeyMap[dataset.name])}
                value={
                  dataset.updated_at
                    ? formatDistanceToNowStrict(new Date(dataset.updated_at), {
                        addSuffix: true,
                        locale: getLocale(lang),
                      })
                    : common('na')
                }
              />
            ))}
          </SectionBody>
        </>
      )}
      {children}
    </PopoverContent>
  );
};

const SectionHeader: FC<PropsWithChildren> = ({ children }) => (
  <p className="px-3 pt-1 text-xxs font-semibold uppercase tracking-wider text-muted-foreground">
    {children}
  </p>
);
const SectionBody: FC<PropsWithChildren> = ({ children }) => (
  <div className="flex flex-col">{children}</div>
);

interface RowProps {
  Icon: AutoneIconComponent;
  label: string;
  value: string;
  status?: DatasetDataFreshnessStatus;
}

const Row: FC<RowProps> = ({ Icon, label, value, status }) => (
  <div className="flex items-center gap-2 px-3 py-2">
    <Icon size="sm" />
    <div className="flex gap-8 items-baseline w-full font-medium text-xs">
      <p className="leading-none mr-auto first-letter:uppercase">{label}</p>
      <p className={status && getFreshnessStatusTextColor(status)}>{value}</p>
    </div>
  </div>
);
